import { useMutation } from '@apollo/client'

import { MUTATION_CREATE_COMMENT } from '@libs/apollo/mutations'

import {
  TASK_WITH_COMMENTS_AND_ACTIVITIES,
  OBSERVATION_WITH_COMMENTS_AND_ACTIVITIES,
  FORM_WITH_COMMENTS_AND_ACTIVITIES,
  PROJECT_FILE_WITH_COMMENTS_AND_ACTIVITIES,
} from '@libs/apollo/queries'

import { LocalFile, MutationCreateCommentArgs, CommentCreateInput, Comment, CommentableType } from '@types'

import { useCreateFile } from '@hooks/file/create-file.hook'

interface Data {
  createComment?: Comment
}

export const useCreateComment = (): {
  createComment: (input: Omit<CommentCreateInput, 'fileId'>, file?: LocalFile) => Promise<Comment | undefined>
} => {
  const { createFile } = useCreateFile()

  const [createComment] = useMutation<Data, MutationCreateCommentArgs>(MUTATION_CREATE_COMMENT, {
    notifyOnNetworkStatusChange: true,
  })

  const _createComment = async (
    input: Omit<CommentCreateInput, 'fileId'>,
    file?: LocalFile,
  ): Promise<Comment | undefined> => {
    try {
      const remoteFile = file ? await createFile(file) : undefined

      const result = await createComment({
        variables: {
          input: {
            commentableId: input.commentableId,
            commentableType: input.commentableType,
            fileId: remoteFile?.id,
            value: input.value,
          },
        },
        awaitRefetchQueries: true,
        refetchQueries:
          input.commentableType == CommentableType.Task
            ? [
                {
                  query: TASK_WITH_COMMENTS_AND_ACTIVITIES,
                  variables: {
                    id: input.commentableId,
                  },
                },
              ]
            : input.commentableType == CommentableType.Observation
            ? [
                {
                  query: OBSERVATION_WITH_COMMENTS_AND_ACTIVITIES,
                  variables: {
                    id: input.commentableId,
                  },
                },
              ]
            : input.commentableType == CommentableType.Form
            ? [
                {
                  query: FORM_WITH_COMMENTS_AND_ACTIVITIES,
                  variables: {
                    id: input.commentableId,
                  },
                },
              ]
            : input.commentableType == CommentableType.ProjectFile
            ? [
                {
                  query: PROJECT_FILE_WITH_COMMENTS_AND_ACTIVITIES,
                  variables: {
                    id: input.commentableId,
                  },
                },
              ]
            : [],
      })

      return result.data?.createComment
    } catch (error) {
      console.error(error)
    }
  }

  return { createComment: _createComment }
}
